import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import type { EventRole } from "../lib/types";

export function useCurrentEventRole(eventId: string | null): {
  role: EventRole | null;
  loading: boolean;
} {
  const [role, setRole] = useState<EventRole | null>(null);
  const [loading, setLoading] = useState(true);
  const instanceRef = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    let cancelled = false;
    if (!eventId) {
      setRole(null);
      setLoading(false);
      return;
    }

    const resolve = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (!cancelled) {
          setRole(null);
          setLoading(false);
        }
        return;
      }

      const { data: event } = await supabase
        .from("events")
        .select("owner_id")
        .eq("id", eventId)
        .maybeSingle();
      if (cancelled) return;

      if (event?.owner_id === user.id) {
        setRole("owner");
        setLoading(false);
        return;
      }

      const userEmailLower = (user.email ?? "").toLowerCase();
      const orFilter = userEmailLower
        ? `user_id.eq.${user.id},email.ilike.${userEmailLower}`
        : `user_id.eq.${user.id}`;
      const { data: fac } = await supabase
        .from("event_facilitators")
        .select("role")
        .eq("event_id", eventId)
        .or(orFilter)
        .maybeSingle();
      if (cancelled) return;

      setRole((fac?.role as EventRole | undefined) ?? null);
      setLoading(false);
    };

    void resolve();

    const channel = supabase
      .channel(`event_facilitators:${eventId}:${instanceRef.current}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_facilitators", filter: `event_id=eq.${eventId}` },
        () => { void resolve(); },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [eventId]);

  return { role, loading };
}
